import { z } from 'zod'

import { camelizeKeys } from '@/functions/utils/camelize-keys'

import { BASKET_TYPE } from '@/components/sales/checkout/consts'

const PaymentsAppointmentDetailsAmountBaseSchema = z.object({
  amount: z.number(),
  amount_formatted: z.string(),
})

const PaymentsAppointmentDetailsStatusBaseSchema = z.object({
  code: z.string(),
  label: z.string(),
})

const BusinessPaymentTypeBaseSchema = z.object({
  code: z.string(),
  source: z.string(),
  label: z.string(),
})

const PaymentsAppointmentDetailsTipsBaseSchema = PaymentsAppointmentDetailsAmountBaseSchema.extend({
  is_platform_fee_excluded: z.boolean(),
})

const PaymentsAppointmentDetailsSummaryBaseSchema = z.object({
  total: PaymentsAppointmentDetailsAmountBaseSchema,
  already_paid: PaymentsAppointmentDetailsAmountBaseSchema,
  remaining: PaymentsAppointmentDetailsAmountBaseSchema,
  tips: PaymentsAppointmentDetailsTipsBaseSchema.nullish(),
})

const AppointmentPaymentBaseSchema = z.object({
  id: z.string(),
  status: PaymentsAppointmentDetailsStatusBaseSchema,
  payment_type: BusinessPaymentTypeBaseSchema,
  amount: z.number(),
  amount_formatted: z.string(),
  created: z.string(),
  basket_id: z.string(),
  basket_type: z.string(),
  transaction_id: z.union([z.number(), z.string()]),
  payment_details_label: z.string(),
  is_refundable: z.boolean().optional(),
  fiscal_receipt: z.unknown().nullable().optional(),
})

const PaymentsAppointmentDetailsBaseSchema = z.object({
  handle_deposit: z.boolean(),
  payable: z.boolean().optional(),
  auto_release_deposit_on_cancel: z.boolean().nullable(),
  payments_summary: PaymentsAppointmentDetailsSummaryBaseSchema,
  appointment_payments: z.array(AppointmentPaymentBaseSchema),
  deposit_status: z.string().nullish(),
})

export const AppointmentPaymentSchema = AppointmentPaymentBaseSchema.transform(camelizeKeys)

const PaymentsAppointmentDetailsAmountSchema = PaymentsAppointmentDetailsAmountBaseSchema.transform(camelizeKeys)
const PaymentsAppointmentDetailsStatusSchema = PaymentsAppointmentDetailsStatusBaseSchema.transform(camelizeKeys)
const PaymentsAppointmentDetailsTipsSchema = PaymentsAppointmentDetailsTipsBaseSchema.transform(camelizeKeys)
const PaymentsAppointmentDetailsSummarySchema = PaymentsAppointmentDetailsSummaryBaseSchema.transform(camelizeKeys)

export const PaymentsAppointmentDetailsSchema = PaymentsAppointmentDetailsBaseSchema.transform((data) => {
  const camelized = camelizeKeys(data)
  const appointmentPayments = data.appointment_payments.map(payment => (
    AppointmentPaymentSchema.parse(payment)
  ))

  return {
    ...camelized,
    appointmentPayments,
    depositInfo: appointmentPayments.find(payment => payment.basketType === BASKET_TYPE.DEPOSIT) ?? null,
    transactionInfo: appointmentPayments.find(payment => payment.basketType === BASKET_TYPE.PAYMENT) ?? null,
  }
})

export const GetPaymentsAppointmentDetailsResponseSchema = z.object({
  result: PaymentsAppointmentDetailsSchema.nullable(),
})

export type PaymentsAppointmentDetailsAmount = z.output<typeof PaymentsAppointmentDetailsAmountSchema>
export type PaymentsAppointmentDetailsTips = z.output<typeof PaymentsAppointmentDetailsTipsSchema>
export type PaymentsAppointmentDetailsStatus = z.output<typeof PaymentsAppointmentDetailsStatusSchema>
export type AppointmentPayment = z.output<typeof AppointmentPaymentSchema>
export type PaymentsAppointmentDetailsSummary = z.output<typeof PaymentsAppointmentDetailsSummarySchema>
export type PaymentsAppointmentDetails = z.output<typeof PaymentsAppointmentDetailsSchema>
export type GetPaymentsAppointmentDetailsResponse = z.output<typeof GetPaymentsAppointmentDetailsResponseSchema>
